import { m } from '@/locale/paraglide/messages';
import {
  getLocale,
  getLocaleCopy,
  isLocaleIndexablePath,
  localizeHref,
  type Locale,
} from '@/lib/locale';
import { Routes } from '@/lib/routes';
import type { MenuItemConfig } from '../types';

const FOOTER_PRODUCT_COPY = {
  en: {
    imageEditor: 'AI Image Editor',
    imageGenerator: 'AI Image Generator',
    imageToVideo: 'Image to Video',
    videoGenerator: 'Text to Video',
  },
  es: {
    imageEditor: 'Editor de imágenes IA',
    imageGenerator: 'Generador de imágenes IA',
    imageToVideo: 'Imagen a video',
    videoGenerator: 'Texto a video',
  },
} satisfies Record<
  Locale,
  {
    imageEditor: string;
    imageGenerator: string;
    imageToVideo: string;
    videoGenerator: string;
  }
>;

function localItem(
  title: string,
  href: string,
  locale: Locale
): MenuItemConfig {
  return {
    title,
    href: localizeHref(href, { locale }),
    external: false,
  };
}

type LocalizedFooterLink = {
  href: string;
  titles: Partial<Record<Locale, string>>;
};

// 每种语言只列出有该语言标题且在该语言可索引的页面
function localizedItems(
  links: LocalizedFooterLink[],
  locale: Locale
): MenuItemConfig[] {
  return links.flatMap(({ href, titles }) => {
    const title = titles[locale];
    return title && isLocaleIndexablePath(href, locale)
      ? [localItem(title, href, locale)]
      : [];
  });
}

const PRODUCT_EXTRA_LINKS: LocalizedFooterLink[] = [
  { href: Routes.PhotoToAnimation, titles: { en: 'Photo to Animation' } },
  { href: Routes.VideoExtend, titles: { en: 'AI Video Extender' } },
  { href: Routes.AiVideoEditor, titles: { en: 'Free AI Video Editor' } },
  { href: Routes.ReferenceToVideo, titles: { en: 'Reference to Video' } },
  { href: Routes.VideoUpscale, titles: { en: 'AI Video Upscaler' } },
  { href: Routes.ImageUpscale, titles: { en: 'AI image upscaler' } },
  {
    href: Routes.ImageStitch,
    titles: {
      en: 'Image stitcher',
      es: 'Unir imágenes',
    },
  },
];

const ALTERNATIVE_LINKS: LocalizedFooterLink[] = [];

// Add long-tail guide pages here; the section hides while it is empty.
const GUIDE_LINKS: LocalizedFooterLink[] = [];

const FOOTER_SECTION_TITLES = {
  en: { alternatives: 'Alternatives', guides: 'Guides' },
  es: { alternatives: 'Alternativas', guides: 'Guías' },
} satisfies Partial<Record<Locale, { alternatives: string; guides: string }>>;

/**
 * Footer links, grouped by section
 */
export function getFooterLinks(locale: Locale = getLocale()): MenuItemConfig[] {
  const copy = getLocaleCopy(FOOTER_PRODUCT_COPY, locale);
  const sectionTitles = getLocaleCopy(FOOTER_SECTION_TITLES, locale);
  const options = { locale };
  const productItems: MenuItemConfig[] = [
    localItem(copy.imageEditor, Routes.Generate, locale),
    localItem(copy.imageGenerator, Routes.ImageGenerator, locale),
    localItem(copy.imageToVideo, Routes.ImageToVideo, locale),
    localItem(copy.videoGenerator, Routes.TextToVideo, locale),
    ...localizedItems(PRODUCT_EXTRA_LINKS, locale),
  ];
  const alternativeItems = localizedItems(ALTERNATIVE_LINKS, locale);
  const guideItems = localizedItems(GUIDE_LINKS, locale);
  const companyItems: MenuItemConfig[] = [
    localItem(m.nav_about_title({}, options), Routes.About, locale),
    localItem(m.nav_contact_title({}, options), Routes.Contact, locale),
  ];
  const legalItems: MenuItemConfig[] = [
    localItem(
      m.nav_cookie_policy_title({}, options),
      Routes.CookiePolicy,
      locale
    ),
    localItem(
      m.nav_privacy_policy_title({}, options),
      Routes.PrivacyPolicy,
      locale
    ),
    localItem(
      m.nav_terms_of_service_title({}, options),
      Routes.TermsOfService,
      locale
    ),
  ];
  return [
    { title: m.nav_product({}, options), items: productItems },
    ...(alternativeItems.length
      ? [{ title: sectionTitles.alternatives, items: alternativeItems }]
      : []),
    ...(guideItems.length
      ? [{ title: sectionTitles.guides, items: guideItems }]
      : []),
    { title: m.nav_company({}, options), items: companyItems },
    { title: m.nav_legal({}, options), items: legalItems },
  ];
}
